
import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { ApplicationResource } from '../../../../common/application/enumeration/ApplicationResource';
import { apiGet, apiPost } from '../../../framework/client';
import { ERROR_API_CALL_FAILED } from '../../../../common/framework/constants';
import { VehicleContractFillRequest } from '../../../../common/application/model/VehicleContractFillRequest';

import SelectField from '../../../../browser/framework/fields/SelectField.vue';
import { getEnumerationOptions, OptionValue } from '../../../framework/service/options';
import {
    GetFillRequestResponse,
    PostFillRequestResponse,
    ValueOf,
    vehicleContractFillRequestHandledApiErrors,
} from '../../../../common/application/service/vehicle_contract_fill_request_service';
import { VehicleEnginePowerType } from '../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleType } from '../../../../common/application/model/enums/VehicleType';
import AutoCompleteMultipleField from '../../../../browser/application/fields/AutoCompleteMultipleField.vue';
import TextField from '../../../../browser/framework/fields/TextField.vue';
import DateField from '../../../../browser/framework/fields/DateField.vue';
import { VehicleElectricalClass } from '../../../../common/application/model/enums/VehicleElectricalClass';
import NumberField from '../../../../browser/framework/fields/NumberField.vue';
import CurrencyAmountField from '../../../../browser/framework/fields/CurrencyAmountField.vue';
import PercentageField from '../../../../browser/framework/fields/PercentageField.vue';
import { SpecialUseVehicleType } from '../../../../common/application/model/enums/SpecialUseVehicleType';
import { errorToast, successToast } from '../../../../browser/application/service/toast_service';
import { sortAlphabetically } from '../../../../browser/application/service/helper_service';

@Component({
    components: {
        PercentageField,
        CurrencyAmountField,
        NumberField,
        DateField,
        TextField,
        AutoCompleteMultipleField,
        SelectField,
        ValidationObserver,
    },
})
export default class VehicleContractFillRequestByUser extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;
    saveInterval: ReturnType<typeof setInterval> | null = null;

    local: {
        loading: boolean;
        saving: boolean;
        error: ValueOf<typeof vehicleContractFillRequestHandledApiErrors> | null;
        row: VehicleContractFillRequest;
        vehicleTypeOptions: OptionValue[];
        vehicleMakeOptions: OptionValue[];
        vehicleEnginePowerTypeOptions: OptionValue[];
        vehicleElectricalClassOptions: OptionValue[];
        specialUseVehicleTypeOptions: OptionValue[];
    } = {
        loading: false,
        saving: false,
        error: null,
        row: null as unknown as VehicleContractFillRequest,
        vehicleTypeOptions: [],
        vehicleMakeOptions: [],
        vehicleEnginePowerTypeOptions: [],
        vehicleElectricalClassOptions: [],
        specialUseVehicleTypeOptions: [],
    };

    // Functions
    async mounted() {
        this.$i18n.locale = this.getLocale;

        const response = await this.getFillRequest(this.getToken);
        if (response.error) {
            this.handleError(response.error);
            return;
        }

        this.local = {
            ...this.local,
            vehicleTypeOptions: getEnumerationOptions('VehicleType', Object.keys(VehicleType), this),
            vehicleEnginePowerTypeOptions: getEnumerationOptions(
                'VehicleEnginePowerType',
                Object.keys(VehicleEnginePowerType),
                this,
            ),
            vehicleElectricalClassOptions: getEnumerationOptions(
                'VehicleElectricalClass',
                Object.keys(VehicleElectricalClass),
                this,
            ),
            specialUseVehicleTypeOptions: getEnumerationOptions(
                'SpecialUseVehicleType',
                Object.keys(SpecialUseVehicleType),
                this,
            ),
            row: response.payload.vehicleContractFillRequest,
            vehicleMakeOptions: response.payload.vehicleMakeOptions.sort(sortAlphabetically('label')),
            loading: false,
        };

        this.saveInterval = setInterval(() => {
            if (!this.local.error) {
                this.saveAsDraft(true);
            }
        }, 20000);
    }

    async destroyed() {
        if (this.saveInterval) {
            clearInterval(this.saveInterval);
        }
    }

    async saveAsDraft(quiet: boolean = false) {
        try {
            await this.postFillRequest(this.getToken, this.local.row);
            if (!quiet) {
                successToast(this, 'message.resourceSaved');
            }
        } catch (e) {
            errorToast(this);
        }
    }

    async saveAndMarkAsCompleted() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmSaveAndMarkAsCompleted').toString(),
            message: this.$t('message.confirmSaveAndMarkAsCompleted').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                this.local.saving = true;

                try {
                    const response = await this.postFillRequest(this.getToken, {
                        ...this.local.row,
                        status: 'PUBLISHED',
                    });

                    if (response.error) {
                        this.handleError(response.error);
                        return;
                    }

                    successToast(this, 'message.resourceSaved');
                    this.local.row = response.payload;
                } catch (e) {
                    errorToast(this);
                } finally {
                    this.local.saving = false;
                }
            },
        });
    }

    async getFillRequest(token: string): Promise<GetFillRequestResponse> {
        const response = await apiGet(`/api/public/vehicle-contract/fill-request?token=${token}`);
        if (response.status === 200) {
            return await response.json();
        } else {
            throw new Error(ERROR_API_CALL_FAILED);
        }
    }

    async postFillRequest(token: string, body: VehicleContractFillRequest): Promise<PostFillRequestResponse> {
        const response = await apiPost(`/api/public/vehicle-contract/fill-request?token=${token}`, body);
        if (response.status === 200) {
            return await response.json();
        } else {
            throw new Error(ERROR_API_CALL_FAILED);
        }
    }

    get getToken(): string {
        return `${this.$route?.query?.token ?? ''}`;
    }

    get getLocale(): string {
        return `${this.$route?.query?.locale ?? 'fi'}`;
    }

    get alreadySubmitted(): boolean {
        return (
            this.local.error === vehicleContractFillRequestHandledApiErrors.AlreadySubmitted ||
            (!this.local.error && this.local.row && this.local.row.status !== 'DRAFT')
        );
    }

    get invalidToken(): boolean {
        return this.local.error === vehicleContractFillRequestHandledApiErrors.InvalidToken;
    }

    get noLongerAvailable(): boolean {
        return this.local.error === vehicleContractFillRequestHandledApiErrors.NoLongerAvailable;
    }

    handleError(error: ValueOf<typeof vehicleContractFillRequestHandledApiErrors>) {
        this.local = {
            ...this.local,
            loading: false,
            saving: false,
            error: error,
        };
    }
}
